import { useMutation } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';

interface ExportAgentData {
  agentUuid: string;
}

export interface AgentFile {
  format: string;
  formatVersion: number;
  exportedAt: string;
  agent: {
    uuid: string;
    data: { title: string; [key: string]: unknown };
    files: Record<string, string>;
  };
  prototype: Record<string, unknown> | null;
}

function toFileName(title: string) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${slug || 'agent'}.agent.json`;
}

function downloadAgentFile(agentFile: AgentFile) {
  const blob = new Blob([JSON.stringify(agentFile, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = toFileName(agentFile.agent.data.title);
  link.click();
  URL.revokeObjectURL(url);
}

export function useExportAgent() {
  const { keycloak } = useKeycloak();

  return useMutation({
    mutationFn: async (exportData: ExportAgentData): Promise<AgentFile> => {
      const response = await fetch(
        `${
          import.meta.env.VITE_IMPORT_ENGINE_ENDPOINT
        }/api/v1/bundles/generate_bundle/functions/export_agent`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`,
          },
          method: 'POST',
          body: JSON.stringify(exportData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'The agent could not be downloaded.');
      }

      return data.data;
    },
    onSuccess: downloadAgentFile,
  });
}
